import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { isMobile } from "react-device-detect";
interface Point {
  label: string;
  value: number;
  color: string;
}

interface DynamicChartProps {
  points: Point[];
}

const generateData = () => {
  const data = [];
  for (let x = 40; x <= 160; x += 1) {
    let y = 0;
    y = Math.pow(Math.sin(((x - 40) / (160 - 40)) * Math.PI), 3.2) * 50;
    data.push({ x, y: Math.max(0, y) });
  }
  return data;
};

const filterDataByRange = (
  data: { x: number; y: number }[],
  min: number,
  max: number
) => {
  return data.filter((d) => d.x >= min && d.x <= max);
};

const CustomizedDot: React.FC<{
  cx: number;
  cy: number;
  payload: any;
  value: number[];
  height: number;
}> = ({ cx, cy, payload, value, ...props }) => {
  if (!payload || !payload.color) return null;

  return (
    <>
      <line
        x1={cx}
        y1={cy}
        x2={cx}
        y2={props?.height + 10}
        stroke={payload.color}
        strokeWidth={2}
      />
      <circle cx={cx} cy={cy} r={6} fill={payload.color} />
    </>
  );
};

const DynamicChart: React.FC<DynamicChartProps> = ({ points }) => {
  const data = generateData();

  const enrichedData = data.flatMap((d) => {
    const overlappingPoints = points.filter((p) => p.value === d.x);

    if (overlappingPoints.length > 0) {
      return overlappingPoints.map((point, index) => ({
        ...d,
        color: point.color,
        label: point.label,
        y: d.y - index * 3,
      }));
    }

    return [d];
  });

  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width={isMobile ? "100%" : 500} height={300}>
        <AreaChart
          data={enrichedData}
          margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
        >
          <CartesianGrid vertical={true} horizontal={false} stroke="#ccc" />
          <XAxis
            dataKey="x"
            type="number"
            domain={[40, 160]}
            ticks={
              isMobile
                ? [40, 60, 80, 100, 120, 140, 160]
                : [40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160]
            }
            tick={{ fill: "#FFFFFF", fontSize: 12 }}
          />
          <YAxis domain={[0, 60]} hide />

          <Area
            type="monotone"
            data={filterDataByRange(data, 40, 55)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#394255"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            data={filterDataByRange(data, 55, 70)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#4f5768"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            data={filterDataByRange(data, 70, 85)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#656c7b"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            data={filterDataByRange(data, 85, 100)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#7b818e"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            data={filterDataByRange(data, 100, 115)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#7B818E"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            data={filterDataByRange(data, 115, 130)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#656C7B"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            data={filterDataByRange(data, 130, 145)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#4F5768"
            strokeWidth={2}
          />

          <Area
            type="monotone"
            data={filterDataByRange(data, 145, 160)}
            dataKey="y"
            stroke="#3F51B5"
            fill="#394255"
            strokeWidth={2}
          />

          <Area
            type="monotone"
            dataKey="y"
            stroke="#3F51B5"
            fill="#00000000"
            strokeWidth={2}
            dot={(props) => <CustomizedDot {...props} />}
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {points.map((point, index) => (
          <div key={index} className="flex items-center gap-2">
            <p
              style={{
                color: point.color,
                fontSize: "14px",
                margin: 0,
              }}
            >
              {point.label}: {point.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DynamicChart;
